import { Alert, Box, Card, CardContent, CardHeader, CircularProgress, Divider, IconButton, List, ListItem, ListItemAvatar, ListItemIcon, ListItemSecondaryAction, ListItemText, Typography, Button } from "@mui/material";
import { TwilioPhoneNumber, TwilioRegStatus, TwilioStepGroup, TwilioStepStatus } from "./models";
import { AccessTimeOutlined, CheckOutlined, ErrorOutline, HourglassEmptyOutlined, PendingOutlined, RefreshOutlined } from "@mui/icons-material";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import NumberPurchaseTable from "./NumberPurchaseTable";
import { useState } from "react";

type Props = {
  statusData: TwilioRegStatus;
  refresh: () => void;
  onAttachNumber: (numberId: string) => void;
  loading: boolean;
};

export default function RegistrationStatus({ statusData, refresh, loading, onAttachNumber }: Props) {
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);

  const getIconGroup = (status: TwilioStepStatus) => {
    return (status === TwilioStepStatus.Completed) ? <CheckOutlined /> : <></>;
  };
  const getIcon = (status: TwilioStepStatus) => {
    switch (status) {
      case TwilioStepStatus.Completed:
        return <CheckOutlined />;
      case TwilioStepStatus.Failed:
        return <ErrorOutline />;
      case TwilioStepStatus.InProgress:
        return <CircularProgress size="14px" />;
      case TwilioStepStatus.ToBeExecuted:
        return <HourglassEmptyOutlined />;
    }
  };

  const getGroupStatus = (group: TwilioStepGroup) => {
    if (statusData.completed || group.steps.every(s => s.status === TwilioStepStatus.Completed)) {
      return TwilioStepStatus.Completed;
    }
    if (group.steps.some(s => s.status === TwilioStepStatus.Failed)) {
      return TwilioStepStatus.Failed;
    }
    if (group.steps.some(s => s.status === TwilioStepStatus.InProgress)) {
      return TwilioStepStatus.InProgress;
    }
    return TwilioStepStatus.ToBeExecuted;
  };

  const currentProgressStep = statusData.stepGroups.map(g => g.steps).flat().find(s => s.status === TwilioStepStatus.InProgress);
  const currentElapsedTime = statusData.nextStepCheckTimer != null
    && statusData.nextStepCheckTimer != -1000
    && statusData.nextStepCheckTimer
    && `~${statusData.nextStepCheckTimer / 1000} sec.`;
  const showNumberSelector = !statusData.phoneNumber && (currentProgressStep?.step === "5.1 Select SMS phone number" || statusData.completed);

  const currentStepIndex = statusData.stepGroups.map(g => g.steps).flat().findIndex(s => s.status === TwilioStepStatus.InProgress);
  const showCampaignApprovalMessage = currentProgressStep?.step === "4.3 Awaiting campaign approval";
  const showSMSAlert = currentStepIndex <= 18;

  return (
    <Box sx={styles.mainContainer}>
      <Card sx={styles.card}>
        <CardHeader
          title="Registration Status"
          action={!statusData.completed && <IconButton onClick={refresh}><RefreshOutlined /></IconButton>}
        />
        <CardContent sx={{ justifyContent: "start", gap: "10px" }}>
          <SpinnerWrapper loading={loading}>
            <List sx={styles.list}>
              {statusData.stepGroups.map((group) => (
                <ListItem key={group.group} sx={styles.listGroup}>
                  <Typography
                    variant="h4"
                    sx={styles.stepText(getGroupStatus(group))}>
                    {group.group} {getIconGroup(getGroupStatus(group))}
                  </Typography>
                  {!statusData.completed && <List sx={styles.list}>
                    {group.steps.filter((step) => step.status == TwilioStepStatus.InProgress || step.status == TwilioStepStatus.Failed).map((step) => (
                      <ListItem key={step.step} sx={styles.substep}>
                        <Typography
                          variant="h5"
                          sx={styles.stepText(step.status, step.step)}>
                          {getIcon(step.status)} {step.step}
                        </Typography>
                        {currentElapsedTime && <Box sx={styles.cardActionBox}><AccessTimeOutlined />{currentElapsedTime}</Box>}
                      </ListItem>
                    ))}
                  </List>}
                  <Divider />
                </ListItem>
              ))}
            </List>
            {statusData.phoneNumber}
            {showNumberSelector && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setPurchaseModalOpen(true)}
              >
                Select number
              </Button>
            )}
            {showNumberSelector && (
              <NumberPurchaseTable
                open={purchaseModalOpen}
                onClose={() => setPurchaseModalOpen(false)}
                onSelect={onAttachNumber}
              />
            )}
            {showSMSAlert && !statusData.completed && !showNumberSelector && <Alert severity="warning" variant="filled">
              During step 3 you will receive an SMS OTP to the mobile number you provided. <b>You must respond to this OTP within 24 hours for your Brand to be approved. If you've failed to respond to this OTP within 24 hours, you can come back to this screen to re-trigger the OTP request.</b>
            </Alert>}
            {showCampaignApprovalMessage && <Alert severity="info" variant="filled">
              This US A2P Campaign is under review, and may take several weeks to complete. You may check back on this Campaign's registration status in a couple of days.
            </Alert>}
          </SpinnerWrapper>
        </CardContent>
      </Card>
    </Box>
  );
}

const styles = {
  card: {

  },
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  list: {
    width: "100%",
  },
  listGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start"
  },
  stepText: (status: string, step: string = "") => ({

    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    color: step.includes("ACTION NEEDED") ? 'red' :
      { [TwilioStepStatus.Failed]: 'red', [TwilioStepStatus.Completed]: 'green' }[status] ?? "initial",
  }),
  cardActionBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "3px",
    fontSize: "11px"
  },
  substep: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  }
};
